import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { smartCityApi } from '@/domains/traffic-monitoring/apis/smart-city';
import BackLink from '@/domains/traffic-monitoring/components/BackLink/BackLink';
import type { EventZone } from '@/domains/traffic-monitoring/types/smart-city.types';
import { cn } from '@/lib/utils';
import Header from '@/shared/components/layout/Header/Header';
import Button from '@/shared/components/ui/Button';
import CheckBox from '@/shared/components/ui/CheckBox/CheckBox';
import Pagination from '@/shared/components/ui/Pagination/Pagination';
import { URLS } from '@/shared/config/urls';

interface ColumnListProps {
  title: string;
  desc: string;
}

const ColumnList = ({ title, desc }: ColumnListProps) => {
  return (
    <div className="flex flex-col gap-2 w-fit">
      <span className="title2 text-text-grey uppercase">{title}</span>

      <span className="title1 text-text-black">{desc}</span>
    </div>
  );
};

interface BoxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Maps an (x,y) point (relative to frame_size) onto the on-screen position
 * inside an `object-fill` <img> inside `container` - accounting for both the
 * detector's frame resolution possibly differing from the image's own pixel
 * size, and the independent x/y stretch `object-fill` applies to fill the
 * container edge-to-edge with no crop and no letterbox bars.
 */
const mapPointToContainer = (
  x: number,
  y: number,
  frameSize: [number, number],
  natural: { width: number; height: number },
  container: { width: number; height: number }
): Point | null => {
  const [frameW, frameH] = frameSize;
  if (
    !frameW ||
    !frameH ||
    !natural.width ||
    !natural.height ||
    !container.width ||
    !container.height
  ) {
    return null;
  }

  const toNaturalX = natural.width / frameW;
  const toNaturalY = natural.height / frameH;

  // object-fill stretches width and height independently (no shared scale,
  // no centering offset), unlike cover/contain which preserve aspect ratio.
  const scaleX = container.width / natural.width;
  const scaleY = container.height / natural.height;

  return {
    x: x * toNaturalX * scaleX,
    y: y * toNaturalY * scaleY,
  };
};

const mapBboxToContainer = (
  bbox: [number, number, number, number],
  frameSize: [number, number],
  natural: { width: number; height: number },
  container: { width: number; height: number }
): BoxRect | null => {
  const [x1, y1, x2, y2] = bbox;
  const start = mapPointToContainer(x1, y1, frameSize, natural, container);
  const end = mapPointToContainer(x2, y2, frameSize, natural, container);
  if (!start || !end) return null;

  return { left: start.x, top: start.y, width: end.x - start.x, height: end.y - start.y };
};

/** `zone.polygon` is a list of [x, y] vertex pairs, same coordinate space as
 *  bbox. A 'counting_line' kind carries only 2 points (a line). */
const mapPolygonToContainer = (
  polygon: [number, number][],
  frameSize: [number, number],
  natural: { width: number; height: number },
  container: { width: number; height: number }
): Point[] | null => {
  if (polygon.length < 2) return null;

  const points: Point[] = [];
  for (const [x, y] of polygon) {
    const point = mapPointToContainer(x, y, frameSize, natural, container);
    if (!point) return null;
    points.push(point);
  }
  return points;
};

// Deterministic color per zone kind, so the same kind always reads the same
// color across zones and across re-renders.
const ZONE_KIND_COLORS = ['#34C759', '#FF9500', '#AF52DE', '#5AC8FA', '#FFCC00', '#FF2D55'];
const DEFAULT_ZONE_COLOR = '#8E8E93';

const colorForZoneKind = (kind: string | null, fallbackIndex: number): string => {
  if (!kind) return ZONE_KIND_COLORS[fallbackIndex % ZONE_KIND_COLORS.length] ?? DEFAULT_ZONE_COLOR;
  let hash = 0;
  for (let i = 0; i < kind.length; i++) {
    hash = (hash * 31 + kind.charCodeAt(i)) | 0;
  }
  return ZONE_KIND_COLORS[Math.abs(hash) % ZONE_KIND_COLORS.length] ?? DEFAULT_ZONE_COLOR;
};

interface EventBoundingBoxProps {
  src: string | undefined;
  bbox: [number, number, number, number] | null;
  frameSize: [number, number] | null;
  zones: EventZone[] | null;
  speedKmh: number | null;
  speedLimitKmh: number | null;
  isLoading?: boolean;
}

const EventBoundingBox = ({
  src,
  bbox,
  frameSize,
  zones,
  speedKmh,
  speedLimitKmh,
  isLoading,
}: EventBoundingBoxProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setContainerSize({ width: rect.width, height: rect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const rect =
    bbox && frameSize ? mapBboxToContainer(bbox, frameSize, naturalSize, containerSize) : null;

  const zonePolygons = frameSize
    ? (zones ?? [])
        .map((zone, index) => ({
          zone,
          color: colorForZoneKind(zone.kind, index),
          points: zone.polygon
            ? mapPolygonToContainer(zone.polygon, frameSize, naturalSize, containerSize)
            : null,
        }))
        .filter((entry): entry is typeof entry & { points: Point[] } => entry.points !== null)
    : [];

  return (
    <div
      ref={containerRef}
      className="relative flex-1 min-h-0 w-full flex justify-center items-center bg-[grey]/20 overflow-hidden rounded-[12px]"
    >
      {isLoading || !src ? (
        <span className="inline-block w-[40px] h-[40px] border-[4px] border-[#007aff]/30 border-t-[#007aff] rounded-full animate-spin" />
      ) : (
        <>
          <img
            ref={imgRef}
            src={src}
            alt="event"
            className="w-full h-full object-fill"
            onLoad={handleImageLoad}
          />
          {rect ? (
            <div
              className="absolute border-2 border-[#FF3B30] pointer-events-none"
              style={{
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
              }}
            />
          ) : null}
          {rect && speedKmh != null ? (
            <div
              className={cn(
                'absolute px-2 py-0.5 rounded-[4px] text-[12px] font-semibold text-white whitespace-nowrap pointer-events-none',
                speedLimitKmh != null && speedKmh > speedLimitKmh ? 'bg-[#FF3B30]' : 'bg-[#007aff]'
              )}
              style={{
                left: rect.left,
                top: Math.max(rect.top - 22, 0),
              }}
            >
              {speedKmh} km/h{speedLimitKmh != null ? ` / limit ${speedLimitKmh}` : ''}
            </div>
          ) : null}
          {zonePolygons.length > 0 ? (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              width={containerSize.width}
              height={containerSize.height}
              aria-hidden="true"
            >
              {zonePolygons.map(({ zone, color, points }, index) => {
                const pointsAttr = points.map((p) => `${p.x},${p.y}`).join(' ');
                const first = points[0];
                return (
                  <g key={`${zone.kind ?? 'zone'}-${zone.name ?? zone.id ?? index}-${index}`}>
                    {points.length === 2 ? (
                      <polyline points={pointsAttr} fill="none" stroke={color} strokeWidth={2} />
                    ) : (
                      <polygon
                        points={pointsAttr}
                        fill={color}
                        fillOpacity={0.15}
                        stroke={color}
                        strokeWidth={2}
                      />
                    )}
                    {zone.kind && first ? (
                      <text
                        x={first.x}
                        y={Math.max(first.y - 6, 10)}
                        fill={color}
                        fontSize={12}
                        fontWeight={600}
                      >
                        {zone.kind}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </svg>
          ) : null}
        </>
      )}
    </div>
  );
};

const EventDetailsDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The row that linked here only hands over its (type, id) pair; everything
  // shown below is fetched fresh from the detail unit rather than carried
  // through the URL.
  const itemType = searchParams.get('type');
  const itemId = searchParams.get('id');
  const lang = i18n.language as 'eng' | 'ind' | 'kor';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['eventDetail', itemType, itemId, lang],
    queryFn: () => smartCityApi.getEventDetail(itemType as string, itemId as string, lang),
    enabled: Boolean(itemType && itemId),
  });
  const detail = data?.data;
  const hasZones = Boolean(detail?.zones?.length);
  const [showZones, setShowZones] = useState(true);

  const currentImage = detail?.image_path
    ? `${URLS.BACK_DSH}/common/image/${detail.image_path}`
    : undefined;

  const eventType = detail?.title ? t(detail.title) : 'N/A';
  const location = detail?.location || 'N/A';
  const dateTime = detail?.timestamp ? detail.timestamp.replace('T', ' ').slice(0, 16) : 'N/A';
  const cctvName = detail?.cctv_name || (detail?.cctv_id ? `CCTV #${detail.cctv_id}` : 'N/A');
  const coordinates =
    detail?.latitude != null && detail?.longitude != null
      ? `${detail.latitude}, ${detail.longitude}`
      : 'N/A';
  const objectClassRaw = detail?.class || detail?.location_type;
  const objectClass = objectClassRaw ? t(objectClassRaw) : 'N/A';
  const speed =
    detail?.speed_kmh != null
      ? `${detail.speed_kmh} km/h${detail.speed_limit_kmh != null ? ` (limit ${detail.speed_limit_kmh} km/h)` : ''}`
      : 'N/A';

  return (
    <section className="_eventDetailDashboard-wrap bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <div className="_eventDetailDashboard-screen p-3 w-full h-full grid grid-cols-[3fr_1fr] gap-3 overflow-hidden">
        <div className="p-6 w-full flex flex-col gap-6 overflow-hidden h-full border rounded-[12px] bg-db-card bg-db-border">
          <div className="flex flex-row items-center justify-between">
            <span className="title1bold capitalize">event details</span>
            {hasZones ? (
              <CheckBox
                label="Show zones"
                checked={showZones}
                onChange={(e) => setShowZones(e.target.checked)}
              />
            ) : null}
          </div>
          <div className="w-full flex-1 min-h-0 flex flex-col gap-3">
            <EventBoundingBox
              src={currentImage}
              bbox={detail?.bbox ?? null}
              frameSize={detail?.frame_size ?? null}
              zones={showZones ? (detail?.zones ?? null) : null}
              speedKmh={detail?.speed_kmh ?? null}
              speedLimitKmh={detail?.speed_limit_kmh ?? null}
              isLoading={isLoading}
            />
          </div>
        </div>
        <div className=" w-full flex flex-col gap-6 overflow-auto h-full  ">
          <div className="flex flex-col gap-6 overflow-auto h-full bg-db-card bg-db-border p-6 border rounded-[12px]">
            {isLoading ? (
              <span className="title2 text-text-grey text-center py-6">Loading…</span>
            ) : isError ? (
              <span className="title2 text-text-grey text-center py-6">
                Failed to load event details
              </span>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <span className="title2 text-text-grey uppercase">Event Type</span>
                  <span className="h5bold">{eventType}</span>
                </div>
                <hr />
                <ColumnList title="CCTV Name" desc={cctvName} />
                <ColumnList title="Event Location" desc={location} />
                <ColumnList title="Latitude, Longitude" desc={coordinates} />
                <hr />
                <ColumnList title="Type" desc={objectClass} />
                <ColumnList title="Speed Detected" desc={speed} />
                <ColumnList title="Event Date & Time" desc={dateTime} />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventDetailsDashboard;
